import {
    Box,
    Button,
    IconButton,
    MenuItem,
    Skeleton,
    Stack,
    Switch,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFormik } from 'formik';
import {
    Check,
    ChevronDown,
    Eye,
    EyeOff,
    Info,
    KeyRound,
    ShieldCheck,
    User as UserIcon,
    Wrench,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import * as Yup from 'yup';
import { httpClient } from '../../common/httpclient';
import { sidebarExpandedSelector } from '../../component/layout/sidebarState';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import { useAppBarTitle } from '../../component/layout/AppBarActions';
import { MobileUserForm } from '../../component/system/MobileSystemForms';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';

/**
 * Seção colapsável do formulário — copiada 1:1 de `suppliers/Form.js`/
 * `products/Form.js` (DESIGN-SYSTEM.md §5.5 "Accordion insertion form" /
 * §6.2).
 */
function AccordionSection({ open, complete, icon: Icon, title, subtitle, onToggle, children }) {
    const theme = useTheme();
    return (
        <Box
            sx={{
                border: '1.5px solid',
                borderColor: open ? 'primary.main' : 'divider',
                borderRadius: `${theme.customRadius.hero}px`,
                overflow: 'hidden',
                backgroundColor: 'background.paper',
                boxShadow: open ? `0 0 0 3px ${theme.palette.info.bg}` : 'none',
                transition: 'border-color .2s, box-shadow .2s',
            }}
        >
            <Box
                onClick={onToggle}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.75, p: '18px 20px', cursor: 'pointer' }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        backgroundImage: theme.palette.gradient.brand,
                        color: '#fff',
                    }}
                >
                    <Icon size={19} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>{title}</Typography>
                    <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
                </Box>
                {complete && (
                    <Box
                        sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            backgroundColor: 'success.main',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Check size={12} strokeWidth={3} />
                    </Box>
                )}
                <ChevronDown
                    size={16}
                    color={theme.palette.text.disabled}
                    style={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }}
                />
            </Box>
            <Box sx={{ maxHeight: open ? 2000 : 0, overflow: 'hidden', transition: 'max-height .35s ease' }}>
                <Box sx={{ p: '16px 20px 24px' }}>{children}</Box>
            </Box>
        </Box>
    );
}

function PreviewRow({ label, value, highlight }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: '12.5px', py: '6px', color: 'text.secondary' }}>
            <span>{label}</span>
            <Box component="b" sx={{ color: highlight ? 'success.main' : 'text.primary', textAlign: 'right', wordBreak: 'break-word' }}>
                {value || '—'}
            </Box>
        </Box>
    );
}

export default function Form() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();
    const isMobileShell = useIsMobileShell();
    useAppBarTitle(isMobileShell ? (id ? 'Editar usuário' : 'Novo usuário') : '');
    // Largura real do menu lateral — usada para alinhar o rodapé de ações fixo
    // com a borda do conteúdo. Ref: `suppliers/Form.js`/`products/Form.js`.
    const isSidebarMobile = useMediaQuery('(max-width:1023.95px)');
    const isSidebarExpanded = useRecoilValue(sidebarExpandedSelector);
    const actionFooterLeft = isSidebarMobile ? 0 : (isSidebarExpanded ? 264 : 76);

    const [profiles, setProfiles] = useState([]);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('success');
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    // Seções do acordeão abertas de forma independente (não exclusiva): cada
    // seção guarda seu próprio estado de expansão, permitindo múltiplas
    // seções abertas ao mesmo tempo.
    const [openSections, setOpenSections] = useState(['personal']);
    const isSectionOpen = (key) => openSections.includes(key);
    const toggleSection = (key) => setOpenSections((prev) => (
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
    const ensureSectionOpen = (key) => setOpenSections((prev) => (
        prev.includes(key) ? prev : [...prev, key]
    ));
    const [showPassword, setShowPassword] = useState(false);
    const [showMasterKey, setShowMasterKey] = useState(false);

    const goBack = () => navigate('/users');

    const fetchProfiles = async () => {
        try {
            const response = await httpClient.get('/profiles');
            setProfiles(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching profiles:', error);
            setProfiles([]);
        }
    };

    // Senha só é obrigatória ao criar — mesma regra do `users/Form.js`
    // anterior (`{!id && (<TextField label="Senha".../>)}`), aqui resolvida
    // reconstruindo o schema a cada render a partir de `id` (mais simples e
    // confiável do que `Yup.when('$isEdit', ...)` + `validationContext`).
    const validationSchema = Yup.object({
        name: Yup.string().required('Nome é obrigatório'),
        last_name: Yup.string().required('Sobrenome é obrigatório'),
        username: Yup.string().required('Nome de usuário é obrigatório'),
        profile: Yup.string().required('Perfil é obrigatório'),
        password: id ? Yup.string() : Yup.string().required('Senha é obrigatória'),
    });

    const handleShowSuccessSnackbar = (text) => {
        setOpenSnackbar(true);
        setMessage(text);
        setSeverity('success');
    };

    const handleShowErrorSnackbar = (responseData) => {
        setOpenSnackbar(true);
        let text = 'Erro desconhecido';
        if (typeof responseData === 'string') {
            text = responseData;
        } else if (responseData && typeof responseData === 'object') {
            text = responseData.message || responseData.error || responseData.id || JSON.stringify(responseData);
        }
        setMessage(text);
        setSeverity('error');
    };

    // Mapeia `profile` (id selecionado no formulário) para `profile_id`,
    // mesma transformação do `users/Form.js` anterior — o resto dos campos
    // segue exatamente como está no formik (inclusive `password`, que o
    // backend simplesmente ignora em `PUT /users/{id}` por não fazer parte
    // de `userUpdateInput`).
    const buildPayload = (values) => {
        const payload = { ...values };
        if (payload.profile) {
            payload.profile_id = payload.profile;
            delete payload.profile;
        }
        return payload;
    };

    const formik = useFormik({
        initialValues: {
            name: '',
            last_name: '',
            username: '',
            password: '',
            profile: '',
            master_key: '',
            is_mechanic: false,
        },
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const payload = buildPayload(values);
                if (id) {
                    const response = await httpClient.put(`/users/${id}`, payload);
                    if (response.status === 204) {
                        handleShowSuccessSnackbar('Usuário atualizado com sucesso!');
                    } else {
                        handleShowErrorSnackbar(response.data);
                    }
                } else {
                    const response = await httpClient.post('/users', payload);
                    if (response.status === 201) {
                        formik.resetForm();
                        setOpenSections(['personal']);
                        handleShowSuccessSnackbar('Usuário cadastrado com sucesso!');
                    } else {
                        handleShowErrorSnackbar(response.data);
                    }
                }
            } catch (error) {
                console.error('Error saving user:', error);
                handleShowErrorSnackbar(error.response?.data || error.message || 'Erro ao salvar usuário');
            } finally {
                setLoading(false);
            }
        },
    });

    // `GET /users/{id}` (fv-api `user.Getter.GetById`) não seleciona
    // `master_key` — o campo "Senha mestre" sempre começa vazio em edição
    // (mesma limitação pré-existente do `users/Form.js` anterior, não
    // corrigida aqui por exigir mudança de SELECT no backend, fora do
    // escopo de um redesign puramente visual). Se o usuário salvar sem
    // preencher esse campo, a chave mestra existente é sobrescrita em
    // branco — comportamento herdado 1:1 do formulário anterior.
    const fetchUser = async () => {
        setLoadingData(true);
        try {
            const response = await httpClient.get(`/users/${id}`);
            const data = response.data || {};
            formik.setValues({
                name: data.name || '',
                last_name: data.last_name || '',
                username: data.username || '',
                password: '',
                profile: data.profile_id || data.profile || '',
                master_key: data.master_key || '',
                is_mechanic: !!data.is_mechanic,
            });
        } catch (error) {
            console.error('Error fetching user:', error);
            handleShowErrorSnackbar('Erro ao carregar dados do usuário');
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => {
        fetchProfiles();
        if (id) {
            fetchUser();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const requiredFieldLabel = (text) => (
        <>
            {text} <Box component="span" sx={{ color: 'error.main' }}>*</Box>
        </>
    );

    // Ref: `suppliers/Form.js`/`products/Form.js` `standaloneFieldSx`/
    // `standaloneInputBoxSx` — mesma casca visual de campo "ff-input" (52px,
    // radius 12px, fundo surface-2, label flutuante interno) aplicada aqui
    // verbatim, inclusive ao `<TextField select>` (Perfil).
    const standaloneInputBoxSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'background.subtle',
            height: '52px',
            transition: 'background-color .15s ease, box-shadow .15s ease',
        },
        '& .MuiOutlinedInput-input:not(textarea):not(.MuiSelect-select)': {
            padding: '21px 14px 7px 14px',
        },
        '& .MuiSelect-select.MuiOutlinedInput-input': {
            padding: '0 14px',
            display: 'flex',
            alignItems: 'center',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1.5px',
            borderColor: 'divider',
        },
        '& .MuiOutlinedInput-notchedOutline legend': {
            display: 'none',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
            borderWidth: '1.5px',
        },
    };

    const standaloneFieldSx = {
        '& .MuiInputBase-input': { fontSize: '13.5px' },
        '& .MuiInputLabel-root': {
            fontSize: '13.5px',
            color: 'text.disabled',
            transform: 'translate(14px, 17px) scale(1)',
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            fontSize: '10px',
            fontWeight: 800,
            color: 'primary.main',
            letterSpacing: '.04em',
            textTransform: 'uppercase',
            transform: 'translate(14px, 8px) scale(1)',
        },
        ...standaloneInputBoxSx,
    };

    // Seções + campos obrigatórios por seção — o anel de progresso é
    // calculado a partir da fração de seções (com campos obrigatórios)
    // completas, mesmo algoritmo de `prototipo-lista-usuarios.html`
    // (`computeProgress()`) — só as 2 seções obrigatórias (Dados pessoais/
    // Dados da conta) entram na conta; Segurança é opcional.
    const isFieldFilled = (key) => {
        const value = formik.values[key];
        if (typeof value === 'boolean') return true;
        return Boolean(String(value ?? '').trim());
    };
    const personalRequiredFields = ['name', 'last_name'];
    const accountRequiredFields = id ? ['username', 'profile'] : ['username', 'profile', 'password'];
    const isPersonalComplete = personalRequiredFields.every(isFieldFilled);
    const isAccountComplete = accountRequiredFields.every(isFieldFilled);
    const requiredSectionsDone = [isPersonalComplete, isAccountComplete].filter(Boolean).length;
    const progressPct = Math.round((requiredSectionsDone / 2) * 100);
    const RING_R = 23;
    const RING_CIRC = 2 * Math.PI * RING_R;
    const ringOffset = RING_CIRC - (RING_CIRC * progressPct / 100);

    const sections = [
        {
            key: 'personal',
            title: 'Dados pessoais',
            subtitle: 'Nome e sobrenome do usuário',
            icon: UserIcon,
            complete: isPersonalComplete,
        },
        {
            key: 'account',
            title: 'Dados da conta',
            subtitle: 'Usuário, senha de acesso e perfil de permissão',
            icon: KeyRound,
            complete: isAccountComplete,
        },
        {
            key: 'security',
            title: 'Segurança',
            subtitle: 'Senha mestre para operações sensíveis (opcional)',
            icon: ShieldCheck,
            complete: false,
        },
    ];

    // Mapa campo → seção do acordeão, usado só para reabrir automaticamente a
    // seção com erro ao tentar salvar — mesmo padrão de `products/Form.js`/
    // `services/Form.js`.
    const standaloneFieldSectionMap = {
        name: 'personal',
        last_name: 'personal',
        username: 'account',
        profile: 'account',
        password: 'account',
    };
    const handleStandaloneSaveClick = async () => {
        const errors = await formik.validateForm();
        const errorFieldKeys = Object.keys(errors);
        if (errorFieldKeys.length > 0) {
            const sectionKeyWithError = errorFieldKeys
                .map((key) => standaloneFieldSectionMap[key])
                .find(Boolean);
            if (sectionKeyWithError) {
                ensureSectionOpen(sectionKeyWithError);
            }
            setMessage('Verifique os campos obrigatórios destacados.');
            setSeverity('error');
            setOpenSnackbar(true);
        }
        formik.handleSubmit();
    };

    // Preview ao vivo — avatar/nome/chips, mesmo padrão de `suppliers/Form.js`.
    const getInitials = () => {
        const first = (formik.values.name || '').trim();
        const last = (formik.values.last_name || '').trim();
        if (first && last) return (first[0] + last[0]).toUpperCase();
        if (first) return first.slice(0, 2).toUpperCase();
        return '?';
    };
    const fullName = `${formik.values.name || ''} ${formik.values.last_name || ''}`.trim();
    const previewName = fullName || 'Nome do usuário';
    const previewSub = formik.values.username ? `@${formik.values.username}` : 'Preencha os dados ao lado';
    const selectedProfileName = profiles.find((p) => p.id === formik.values.profile)?.name || '';
    const isAdminProfile = selectedProfileName === 'Administrador';

    // Shell mobile: formulário do protótipo `sistema/usuario-form.html` (mesmos campos, validação e envio).
    if (isMobileShell) {
        return (
            <PageScaffold
                titlePageTarget="Usuários"
                links={[{ title: 'Usuários', link: '/users' }]}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={message}
                severity={severity}
            >
                {loadingData ? (
                    <Stack spacing={1.25}>
                        {[1, 2, 3].map((n) => <Skeleton key={n} variant="rounded" height={64} sx={{ borderRadius: '16px' }} />)}
                    </Stack>
                ) : (
                    <MobileUserForm
                        formik={formik}
                        profiles={profiles}
                        isEdit={Boolean(id)}
                        showPassword={showPassword}
                        onTogglePassword={() => setShowPassword((v) => !v)}
                        showMasterKey={showMasterKey}
                        onToggleMasterKey={() => setShowMasterKey((v) => !v)}
                        onCancel={goBack}
                        onSave={handleStandaloneSaveClick}
                        loading={loading}
                    />
                )}
            </PageScaffold>
        );
    }

    if (loadingData) {
        return (
            <PageScaffold
                titlePageTarget="Usuários"
                links={[{ title: 'Usuários', link: '/users' }]}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={message}
                severity={severity}
            >
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px' }}>
                    <Stack spacing={1.75}>
                        <Skeleton variant="rounded" height={180} sx={{ borderRadius: '18px' }} />
                        <Skeleton variant="rounded" height={260} sx={{ borderRadius: '18px' }} />
                        <Skeleton variant="rounded" height={140} sx={{ borderRadius: '18px' }} />
                    </Stack>
                    <Skeleton variant="rounded" height={380} sx={{ borderRadius: '20px' }} />
                </Box>
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            titlePageTarget="Usuários"
            links={[{ title: 'Usuários', link: '/users' }]}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={message}
            severity={severity}
        >
            <Box>
                {/* form-hero — título + descrição + anel de progresso. */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '20px',
                        flexWrap: 'wrap',
                        mb: 2.25,
                    }}
                >
                    <Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
                            {id ? 'Editar usuário' : 'Novo usuário'}
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: 'text.secondary' }}>
                            Preencha por seções — o cartão ao lado mostra o resultado em tempo real.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
                        <Box sx={{ position: 'relative', width: 54, height: 54, flexShrink: 0 }}>
                            <svg viewBox="0 0 54 54" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                                <circle cx="27" cy="27" r={RING_R} fill="none" strokeWidth="5" stroke={theme.palette.divider} />
                                <circle
                                    cx="27"
                                    cy="27"
                                    r={RING_R}
                                    fill="none"
                                    strokeWidth="5"
                                    strokeLinecap="round"
                                    stroke={theme.palette.primary.main}
                                    strokeDasharray={RING_CIRC}
                                    strokeDashoffset={ringOffset}
                                    style={{ transition: 'stroke-dashoffset .35s ease' }}
                                />
                            </svg>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 12,
                                    fontWeight: 800,
                                }}
                            >
                                {progressPct}%
                            </Box>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Cadastro</Typography>
                            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                completo conforme você preenche
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* step-pills */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.25 }}>
                    {sections.map((sec, idx) => {
                        const isSecOpen = isSectionOpen(sec.key);
                        return (
                            <Box
                                key={sec.key}
                                onClick={() => ensureSectionOpen(sec.key)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '7px',
                                    px: '14px',
                                    py: '8px',
                                    borderRadius: '24px',
                                    border: '1.5px solid',
                                    borderColor: isSecOpen ? 'primary.main' : 'divider',
                                    backgroundColor: isSecOpen ? 'info.bg' : 'background.paper',
                                    color: isSecOpen ? 'primary.main' : 'text.secondary',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 17,
                                        height: 17,
                                        borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: sec.complete ? 'success.main' : (isSecOpen ? 'primary.main' : 'text.disabled'),
                                        backgroundColor: sec.complete ? 'success.main' : 'transparent',
                                        color: sec.complete ? '#fff' : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 9,
                                        flexShrink: 0,
                                    }}
                                >
                                    {sec.complete ? <Check size={10} strokeWidth={3} /> : idx + 1}
                                </Box>
                                {sec.title}
                            </Box>
                        );
                    })}
                </Box>

                {/* form-columns — acordeão + preview sticky */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px', alignItems: 'start', '& > *': { minWidth: 0 } }}>
                    <Stack spacing={1.75} sx={{ minWidth: 0 }} component="form" onSubmit={formik.handleSubmit}>
                        <AccordionSection
                            open={isSectionOpen('personal')}
                            complete={sections[0].complete}
                            icon={sections[0].icon}
                            title={sections[0].title}
                            subtitle={sections[0].subtitle}
                            onToggle={() => toggleSection('personal')}
                        >
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, '& > *': { minWidth: 0 } }}>
                                <TextField
                                    autoFocus
                                    label={requiredFieldLabel('Nome')}
                                    fullWidth
                                    name="name"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                    helperText={formik.touched.name && formik.errors.name}
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                />
                                <TextField
                                    label={requiredFieldLabel('Sobrenome')}
                                    fullWidth
                                    name="last_name"
                                    value={formik.values.last_name}
                                    onChange={formik.handleChange}
                                    error={formik.touched.last_name && Boolean(formik.errors.last_name)}
                                    helperText={formik.touched.last_name && formik.errors.last_name}
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                />
                            </Box>
                        </AccordionSection>

                        <AccordionSection
                            open={isSectionOpen('account')}
                            complete={sections[1].complete}
                            icon={sections[1].icon}
                            title={sections[1].title}
                            subtitle={sections[1].subtitle}
                            onToggle={() => toggleSection('account')}
                        >
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, mb: 2, '& > *': { minWidth: 0 } }}>
                                <TextField
                                    label={requiredFieldLabel('Nome de usuário')}
                                    fullWidth
                                    name="username"
                                    autoComplete="off"
                                    value={formik.values.username}
                                    onChange={formik.handleChange}
                                    error={formik.touched.username && Boolean(formik.errors.username)}
                                    helperText={formik.touched.username && formik.errors.username}
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                />
                                <TextField
                                    select
                                    label={requiredFieldLabel('Perfil')}
                                    fullWidth
                                    name="profile"
                                    value={formik.values.profile}
                                    onChange={formik.handleChange}
                                    error={formik.touched.profile && Boolean(formik.errors.profile)}
                                    helperText={formik.touched.profile && formik.errors.profile}
                                    variant="outlined"
                                    // Select com label flutuante (não static-micro): reserva o topo
                                    // do valor como nos inputs, senão o texto centralizado encosta
                                    // no label encolhido.
                                    sx={{
                                        ...standaloneFieldSx,
                                        '& .MuiSelect-select.MuiOutlinedInput-input': {
                                            padding: '21px 32px 7px 14px',
                                            display: 'block',
                                        },
                                    }}
                                >
                                    {profiles.map((profile) => (
                                        <MenuItem key={profile.id} value={profile.id}>{profile.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Box>

                            {!id ? (
                                <Box sx={{ mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        label={requiredFieldLabel('Senha')}
                                        name="password"
                                        type={showPassword ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        value={formik.values.password}
                                        onChange={formik.handleChange}
                                        error={formik.touched.password && Boolean(formik.errors.password)}
                                        helperText={formik.touched.password && formik.errors.password}
                                        variant="outlined"
                                        sx={standaloneFieldSx}
                                        InputProps={{
                                            endAdornment: (
                                                <IconButton
                                                    onClick={() => setShowPassword((v) => !v)}
                                                    edge="end"
                                                    size="small"
                                                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                                    sx={{ mr: 0.5 }}
                                                >
                                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                                </IconButton>
                                            ),
                                        }}
                                    />
                                </Box>
                            ) : (
                                <Typography sx={{ fontSize: 11, color: 'text.disabled', mb: 2 }}>
                                    A senha não é alterada por aqui — para redefini-la, use uma ação administrativa separada.
                                </Typography>
                            )}

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '14px',
                                    pt: '14px',
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Wrench size={17} color={formik.values.is_mechanic ? theme.palette.primary.main : theme.palette.text.disabled} />
                                    <Box>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>É um Mecânico</Typography>
                                        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                            Disponibiliza este usuário para ser atribuído como mecânico responsável em ordens de serviço
                                        </Typography>
                                    </Box>
                                </Box>
                                <Switch
                                    checked={formik.values.is_mechanic}
                                    onChange={(e) => formik.setFieldValue('is_mechanic', e.target.checked)}
                                />
                            </Box>
                        </AccordionSection>

                        <AccordionSection
                            open={isSectionOpen('security')}
                            complete={sections[2].complete}
                            icon={sections[2].icon}
                            title={sections[2].title}
                            subtitle={sections[2].subtitle}
                            onToggle={() => toggleSection('security')}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '10px',
                                    backgroundColor: 'info.bg',
                                    color: 'primary.main',
                                    borderRadius: '12px',
                                    p: '12px 14px',
                                    fontSize: 12,
                                    lineHeight: 1.5,
                                    mb: 2,
                                }}
                            >
                                <Info size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                                A senha mestre é usada para autorizar operações sensíveis, como cancelar itens, alterar quantidade de produtos e cancelar vendas.
                            </Box>

                            <TextField
                                fullWidth
                                label="Senha mestre"
                                name="master_key"
                                type={showMasterKey ? 'text' : 'password'}
                                autoComplete="new-password"
                                value={formik.values.master_key}
                                onChange={formik.handleChange}
                                variant="outlined"
                                sx={standaloneFieldSx}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            onClick={() => setShowMasterKey((v) => !v)}
                                            edge="end"
                                            size="small"
                                            aria-label={showMasterKey ? 'Ocultar senha mestre' : 'Mostrar senha mestre'}
                                            sx={{ mr: 0.5 }}
                                        >
                                            {showMasterKey ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </IconButton>
                                    ),
                                }}
                            />
                        </AccordionSection>
                    </Stack>

                    {/* preview sticky */}
                    <Box sx={{ position: { md: 'sticky' }, top: { md: 20 } }}>
                        <Typography
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '10.5px',
                                fontWeight: 800,
                                textTransform: 'uppercase',
                                letterSpacing: '.06em',
                                color: 'text.disabled',
                                mb: 1.25,
                            }}
                        >
                            <Eye size={13} /> Pré-visualização ao vivo
                        </Typography>

                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: `${theme.customRadius.hero}px`,
                                backgroundColor: 'background.paper',
                                boxShadow: theme.customShadows.md,
                                overflow: 'hidden',
                                mb: 1.75,
                            }}
                        >
                            <Box sx={{ height: 78, backgroundImage: theme.palette.gradient.brand, position: 'relative' }}>
                                <Box
                                    sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: '22px',
                                        backgroundColor: 'background.paper',
                                        border: '4px solid',
                                        borderColor: 'background.paper',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 22,
                                        fontWeight: 800,
                                        color: 'primary.main',
                                        position: 'absolute',
                                        left: 20,
                                        bottom: -32,
                                        boxShadow: theme.customShadows.md,
                                    }}
                                >
                                    {getInitials()}
                                </Box>
                            </Box>
                            <Box sx={{ p: '42px 20px 20px' }}>
                                <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}>
                                    {previewName}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.25 }}>
                                    {previewSub}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.5 }}>
                                    {selectedProfileName && (
                                        <Box
                                            component="span"
                                            sx={{
                                                fontSize: 11,
                                                fontWeight: 700,
                                                px: '10px',
                                                py: '5px',
                                                borderRadius: '20px',
                                                backgroundColor: isAdminProfile ? 'error.bg' : 'secondary.bg',
                                                color: isAdminProfile ? 'error.main' : 'secondary.main',
                                            }}
                                        >
                                            {selectedProfileName}
                                        </Box>
                                    )}
                                    {formik.values.is_mechanic && (
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '5px',
                                                fontSize: 11,
                                                fontWeight: 700,
                                                px: '10px',
                                                py: '5px',
                                                borderRadius: '20px',
                                                backgroundColor: 'success.bg',
                                                color: 'success.main',
                                            }}
                                        >
                                            <Wrench size={11} /> Mecânico
                                        </Box>
                                    )}
                                </Box>
                                <Box sx={{ height: '1px', backgroundColor: 'divider', my: 2 }} />
                                <PreviewRow label="Perfil" value={selectedProfileName} />
                                <PreviewRow label="Usuário" value={formik.values.username} />
                                <PreviewRow
                                    label="Senha mestre"
                                    value={formik.values.master_key ? 'Definida' : 'Não definida'}
                                    highlight={Boolean(formik.values.master_key)}
                                />
                            </Box>
                        </Box>

                        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                            Campos com <Box component="span" sx={{ color: 'error.main' }}>*</Box> são obrigatórios. Você pode editar tudo depois de salvar.
                        </Typography>
                    </Box>
                </Box>

                {/* Espaçador para compensar o rodapé fixo abaixo. */}
                <Box sx={{ height: 'calc(84px + env(safe-area-inset-bottom, 0px))' }} aria-hidden="true" />

                {/* action-footer fixo — Cancelar/Salvar. */}
                <Box
                    sx={{
                        position: 'fixed',
                        left: actionFooterLeft,
                        right: 0,
                        bottom: 0,
                        zIndex: theme.zIndex.appBar,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                        backgroundColor: 'background.paper',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 -6px 20px rgba(15,23,42,.06)',
                        px: 3,
                        py: 1.75,
                        pl: 'calc(24px + env(safe-area-inset-left, 0px))',
                        pr: 'calc(24px + env(safe-area-inset-right, 0px))',
                        pb: 'calc(14px + env(safe-area-inset-bottom, 0px))',
                        transition: 'left .2s ease',
                    }}
                >
                    <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>
                        Campos com * são obrigatórios
                    </Typography>
                    <Stack direction="row" spacing={1.5}>
                        <Button variant="outlined" onClick={goBack}>
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleStandaloneSaveClick}
                            disabled={loading}
                        >
                            {loading ? 'Salvando...' : (id ? 'Salvar alterações' : 'Salvar usuário')}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </PageScaffold>
    );
}
